import type { GoogleHealthPayload } from "../types/googleHealthBridge.types";
import { GOOGLE_HEALTH_EVENT } from "../types/googleHealthBridge.types";
import { getGluceraUserIdFromStorage } from "./gluceraAuthCookies";
import { getGluceraAccessToken } from "./storage";
import {
  isGluceraFlutterApp,
  requestGoogleHealthDataFromFlutter,
} from "./flutterHealthBridge";

export const isGluceraAndroidApp = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.GluceraAndroid?.requestGoogleHealthData === "function";

export const isGluceraNativeApp = (): boolean =>
  isGluceraAndroidApp() || isGluceraFlutterApp();

export const parseGoogleHealthPayload = (
  raw: GoogleHealthPayload | string | unknown,
): GoogleHealthPayload | null => {
  if (!raw) return null;

  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as Record<string, unknown>;
  if (!Array.isArray(obj.health_data)) return null;

  return obj as GoogleHealthPayload;
};

export const dispatchGoogleHealthPayload = (payload: GoogleHealthPayload): void => {
  window.dispatchEvent(
    new CustomEvent(GOOGLE_HEALTH_EVENT, { detail: payload }),
  );
};

/** Register callbacks Android invokes via evaluateJavascript. */
export const setupGluceraWebCallbacks = (): void => {
  if (typeof window === "undefined") return;

  window.GluceraWeb = {
    ...window.GluceraWeb,
    getAuthContext: () =>
      JSON.stringify({
        userId: getGluceraUserIdFromStorage(),
        accessToken: getGluceraAccessToken(),
      }),
    onHealthData: (raw) => {
      const payload = parseGoogleHealthPayload(raw);
      if (payload) {
        dispatchGoogleHealthPayload(payload);
        return;
      }
      window.GluceraWeb?.onHealthDataError?.("Invalid health data from app");
    },
    onHealthDataError: (message) => {
      window.dispatchEvent(
        new CustomEvent("glucera:health-data-error", { detail: message }),
      );
    },
  };
};

export const requestGoogleHealthData = (): boolean => {
  if (isGluceraAndroidApp()) {
    try {
      window.GluceraAndroid!.requestGoogleHealthData!();
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

/** Android (sync) or Flutter (async via callHandler). */
export const requestNativeGoogleHealthData = async (): Promise<{
  ok: boolean;
  raw: unknown;
}> => {
  if (isGluceraAndroidApp()) {
    return { ok: requestGoogleHealthData(), raw: null };
  }
  return requestGoogleHealthDataFromFlutter();
};

export const subscribeGoogleHealthData = (
  handler: (payload: GoogleHealthPayload) => void,
): (() => void) => {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<GoogleHealthPayload>).detail;
    const payload = parseGoogleHealthPayload(detail);
    if (payload) handler(payload);
  };

  window.addEventListener(GOOGLE_HEALTH_EVENT, listener);
  return () => window.removeEventListener(GOOGLE_HEALTH_EVENT, listener);
};

export const subscribeGoogleHealthError = (
  handler: (message: string) => void,
): (() => void) => {
  const listener = (event: Event) => {
    handler(String((event as CustomEvent<string>).detail ?? "Sync failed"));
  };
  window.addEventListener("glucera:health-data-error", listener);
  return () => window.removeEventListener("glucera:health-data-error", listener);
};
